import { useCallback, useId, type ReactNode } from "react";
import type { BuilderField, FormAnswer } from "@froppio/form-core";
import type { FormRendererComponents } from "./types";
import { SignatureInput } from "./SignatureInput";

export interface FormPreviewFieldProps {
  components: FormRendererComponents;
  field: BuilderField;
  value: FormAnswer | undefined;
  error?: string;
  onChange: (fieldId: string, value: FormAnswer) => void;
  onControlError: (fieldId: string, error: string | undefined) => void;
}

export function FormPreviewField({ components, field, value, error, onChange, onControlError }: FormPreviewFieldProps) {
  const { Button, Input, Select, TextArea, FormField } = components;
  const instanceId = useId();
  const id = `${instanceId}-${field.id}`;
  // Disassociate preview controls from a surrounding editor form and its native validation.
  const form = `${instanceId}-preview-only`;
  const label = field.label.trim() ? field.label : "Untitled question";
  const describedBy = [field.helpText ? `${id}-description` : undefined, error ? `${id}-error` : undefined].filter(Boolean).join(" ") || undefined;
  const controlError = useCallback((message: string | undefined) => onControlError(field.id, message), [field.id, onControlError]);
  const change = (answer: FormAnswer) => onChange(field.id, answer);
  const textValue = typeof value === "string" ? value : "";

  function group(children: ReactNode) {
    return (
      <fieldset className="min-w-0 space-y-[7px]" aria-describedby={describedBy} aria-invalid={!!error}>
        <legend className="text-sm font-semibold text-foreground">
          {label} {field.required ? <span aria-hidden="true">*</span> : <span className="text-xs font-normal text-muted-foreground">Optional</span>}
          {field.required && <span className="sr-only"> (required)</span>}
        </legend>
        {children}
        {field.helpText && <p id={`${id}-description`} className="text-xs leading-relaxed text-muted-foreground">{field.helpText}</p>}
        {error && <p id={`${id}-error`} role="alert" className="text-sm text-destructive">{error}</p>}
      </fieldset>
    );
  }

  if (field.widget === "signature") {
    return group(<SignatureInput components={components} id={id} label={label} describedBy={describedBy} invalid={!!error} onChange={change} onError={controlError} />);
  }

  if (field.widget === "radio" || field.widget === "multipleChoice" || field.widget === "yesNo") {
    const multiple = field.widget === "multipleChoice";
    const choices = field.widget === "yesNo"
      ? [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]
      : field.choices;
    return group(
      <div className="space-y-1">
        {choices.map((choice, index) => {
          const answer = field.widget === "yesNo" ? choice.value === "yes" : choice.value;
          const checked = multiple ? Array.isArray(value) && value.includes(choice.value) : value === answer;
          return (
            <label key={`${choice.value}-${index}`} className="flex min-h-11 cursor-pointer items-center gap-3 text-sm text-foreground">
              <input
                type={multiple ? "checkbox" : "radio"}
                name={id}
                form={form}
                value={choice.value}
                checked={checked}
                aria-describedby={describedBy}
                aria-invalid={!!error}
                className="size-5 shrink-0 accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onChange={event => {
                  if (!multiple) change(answer);
                  else {
                    const selected = Array.isArray(value) ? value : [];
                    change(event.target.checked ? [...selected, choice.value] : selected.filter(entry => entry !== choice.value));
                  }
                }}
              />
              <span>{choice.label.trim() ? choice.label : `Untitled option ${index + 1}`}</span>
            </label>
          );
        })}
        {!choices.length && <p className="text-xs text-muted-foreground">No options have been added yet.</p>}
        {!multiple && <Button type="button" size="sm" variant="quiet" onClick={() => change("")} aria-label={`Clear answer for ${label}`}>Clear answer</Button>}
      </div>,
    );
  }

  return (
    <FormField id={id} label={label} description={field.helpText} required={field.required} optional={!field.required} error={error}>
      {field.widget === "dropdown" ? (
        <Select
          form={form}
          value={textValue}
          onChange={event => change(event.target.value)}
        >
          <option value="">Choose an option</option>
          {field.choices.map((choice, index) => <option key={`${choice.value}-${index}`} value={choice.value}>{choice.label.trim() ? choice.label : `Untitled option ${index + 1}`}</option>)}
        </Select>
      ) : field.widget === "longText" ? (
        <TextArea form={form} value={textValue} onChange={event => change(event.target.value)} autoComplete="off" />
      ) : (
        <Input
          form={form}
          type={field.widget === "phone" ? "tel" : field.widget === "email" ? "email" : "text"}
          inputMode={field.widget === "number" ? "decimal" : undefined}
          placeholder={field.widget === "date" ? "YYYY-MM-DD" : undefined}
          aria-label={field.widget === "date" ? `${label} (YYYY-MM-DD)` : undefined}
          value={textValue}
          autoComplete="off"
          onChange={event => change(event.target.value)}
        />
      )}
    </FormField>
  );
}
